/**
 * MiniCart.tsx — Mini Cart Block (Visual Editor version)
 * 
 * Wired to the cart store — shows real item count, opens cart drawer on click.
 * Used when a MiniCart is placed via the visual editor (as opposed to
 * cart/MiniCart.tsx which is hardcoded into the Header layout).
 */
import React, { useEffect, useState } from 'react';
import { isB2bHideCart } from '@agentic/magento-client/lib/feature-flags';

interface MiniCartProps {
  data: {
    showBadge?: boolean;
    showDropdown?: boolean;
    variant?: string;
    [key: string]: unknown;
  };
  styles?: string;
}

export const MiniCart: React.FC<MiniCartProps> = ({ data, styles }) => {
  const { showBadge = true } = data ?? {};
  const [itemCount, setItemCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    // Dynamically import the cart store to avoid SSR issues
    void import('@agentic/magento-client/stores/cart').then((cartStore) => {
      // Subscribe to cart item count
      const unsubCount = cartStore.cartItemCount.subscribe((count: number) => {
        setItemCount(count);
      });
      const unsubLoading = cartStore.isCartLoading.subscribe((loading: boolean) => {
        setIsLoading(loading);
      });

      // Initialize the cart
      cartStore.initCart();

      return () => {
        unsubCount();
        unsubLoading();
      };
    });
  }, []);

  // Hide the cart entirely when the merchant has turned the store into a
  // quote-only catalogue (B2B Suite → Hide Cart). Mirrors MiniQuoteCart's
  // feature-flag gate; placed after all hooks so hook order stays stable.
  if (isB2bHideCart()) return null;

  const handleClick = () => {
    void import('@agentic/magento-client/stores/cart').then((cartStore) => {
      cartStore.openCart();
    });
  };

  interface MiniCartSettings {
    pillBackground?: string;
    pillBorder?: string;
    pillRadius?: string;
    pillPadding?: string;
    iconSize?: number;
    color?: string;
    variant?: string;
  }
  const dataSettings = data?.settings;
  const settingsOrData = (typeof dataSettings === 'object' && dataSettings !== null
    ? dataSettings
    : (data ?? {})) as MiniCartSettings;
  const {
    pillBackground = 'var(--color-header-icon-bg, var(--color-header-search-bg, rgba(255,255,255,0.06)))',
    pillBorder = '1px solid var(--color-header-icon-border, var(--color-header-search-border, var(--glass-border, rgba(255,255,255,0.18))))',
    pillRadius = 'var(--style-btn-radius, 9999px)',
    pillPadding = '4px 14px',
    iconSize,
    color = '',
    variant = 'standard'
  } = settingsOrData;

  // expansion: real variants
  // 'icon-badge' (default — icon + count badge corner)
  // 'icon-text-pill' — icon + 'Cart (3)' inside the pill
  // 'icon-only' — bare icon, no badge
  // 'solid_box' — legacy 'BASKET' filled box style (kept for backwards compat)
  // 'subtotal-inline' — icon + "3 items" inline; mobile collapses to icon-badge
  const isSolidBox = variant === 'solid_box';
  const isTextPill = variant === 'icon-text-pill';
  const isIconOnly = variant === 'icon-only';
  const isSubtotalInline = variant === 'subtotal-inline';
  const buttonLabel: string = (data?.label as string | undefined) ?? ((data?.content as { label?: string } | undefined)?.label) ?? 'Cart';

  const pillStyle: React.CSSProperties = isSolidBox ? {
    backgroundColor: 'var(--surface-1)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--style-btn-radius, 4px)',
    padding: '8px 16px',
    minHeight: '44px',
    // Solid-box variant sits on the inner surface, not chrome — use body token.
    color: 'var(--text-primary)',
    fontWeight: 'bold',
    // Shadow tracks the active style preset's button shadow (Core ships a
    // subtle 1px lift; Tech Showcase ships a neon glow; etc.). Replaces the
    // dead Tailwind `shadow-sm` that used to be on the className.
    boxShadow: 'var(--style-btn-primary-shadow, 0 1px 2px 0 rgba(0,0,0,0.05))',
  } : (isTextPill || isSubtotalInline) ? {
    // Same rationale as the icon-only branch below — expose
    // pillBackground via CSS variable so the :hover rule can override it.
    ['--color-header-icon-bg' as string]: pillBackground,
    border: pillBorder,
    borderRadius: pillRadius,
    padding: '8px 14px',
    minHeight: '36px',
    // Color is driven by .header-icon-btn CSS class (resting + :hover).
    // Setting `color` inline here would beat the :hover rule (specificity 1,0,0,0
    // vs 0,2,0) and freeze the icon to resting state on hover. If a user-provided
    // override is set, expose it as the --color-header-icon CSS variable so the
    // class rule reads it for resting AND the :hover rule still wins.
    ...(color ? { ['--color-header-icon' as string]: color } : {}),
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    fontWeight: 500,
  } : {
    // 2026-05-28 PR G5 round 5 — was setting --color-header-icon-bg
    // inline to pillBackground, where pillBackground defaults to
    // `var(--color-header-icon-bg, var(--color-header-search-bg, ...))`.
    // That makes the inline CSS variable redefine itself — a CIRCULAR
    // REFERENCE at the button scope. Chrome resolves circular vars to
    // invalid → initial → transparent. Verified live on cable: cart bg
    // was rgba(0,0,0,0) while wishlist + quote (no inline override)
    // showed rgba(255,255,255,0.10) from the :root cascade.
    //
    // The HEADER chrome on cable renders THIS file
    // (blocks/global/MiniCart.tsx) via the structure preset's
    // mini_cart block, NOT cart/MiniCart.tsx — verified by the
    // duration-200 class hash on live matching this file's className.
    //
    // Correct pattern: don't set --color-header-icon-bg inline at all.
    // The .header-icon-btn class reads the value from :root via normal
    // cascade. Merchants who want a per-block override should set it
    // via the structure preset's chrome-token slot, not via the block's
    // inline pillBackground prop.
    border: pillBorder,
    borderRadius: pillRadius,
    padding: pillPadding,
    minHeight: '32px',
    // Color is driven by .header-icon-btn class — DO NOT set inline `color` here,
    // it would block the :hover state. Expose any user-provided override as the
    // --color-header-icon CSS variable instead.
    ...(color ? { ['--color-header-icon' as string]: color } : {}),
  };

  // Use 0 during SSR/hydration to match server HTML, real count only after mount
  const displayCount = mounted ? itemCount : 0;

  return (
    <div className={`relative ${styles ?? ''}`}>
      <button
        onClick={handleClick}
        className={`relative flex items-center justify-center transition-all duration-300 ${isSolidBox ? 'hover:bg-[var(--surface-2)]' : 'header-icon-btn'}`}
        style={pillStyle}
        aria-label={`Cart with ${displayCount} items`}
      >
        {isLoading ? (
          <svg
            style={{
              width: iconSize ? `${iconSize}px` : 'var(--style-header-icon-size, 22px)',
              height: iconSize ? `${iconSize}px` : 'var(--style-header-icon-size, 22px)',
            }}
            className="animate-spin" fill="none" viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        ) : (
          <div className="flex items-center gap-2">
            <svg
              style={{
                width: isSolidBox ? '20px' : iconSize ? `${iconSize}px` : 'var(--style-header-icon-size, 22px)',
                height: isSolidBox ? '20px' : iconSize ? `${iconSize}px` : 'var(--style-header-icon-size, 22px)',
              }}
              fill="none" viewBox="0 0 24 24" stroke="currentColor"
            >
              <path strokeLinecap="square" strokeLinejoin="miter" strokeWidth={isSolidBox ? 2 : 1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
            {isSolidBox && <span className="text-[14px]">Basket</span>}
            {isTextPill && (
              <span className="text-[13px] font-medium">
                {buttonLabel}{displayCount > 0 ? ` (${displayCount})` : ''}
              </span>
            )}
            {isSubtotalInline && (
              <span className="hidden sm:inline text-[13px] font-medium whitespace-nowrap">
                {displayCount === 0 ? 'Cart' : `${displayCount} item${displayCount === 1 ? '' : 's'}`}
              </span>
            )}
          </div>
        )}
        {showBadge && !isIconOnly && !isTextPill && !isSubtotalInline && displayCount > 0 && (
          <span
            className={`absolute ${isSolidBox ? '-top-2 -right-2' : '-top-1.5 -right-1.5'} font-extrabold text-[10px] rounded-full w-5 h-5 flex items-center justify-center border-2`}
            style={{
              backgroundColor: 'var(--color-global-badge-bg, var(--accent-primary))',
              color: 'var(--color-global-badge-text, #ffffff)',
              // Badge border = footer/chrome bg so it punches a clean hole
              // through the icon. Matches the WishlistIcon + MiniQuoteCart
              // pattern at their badge spans (lines 119 / 127 respectively).
              borderColor: isSolidBox
                ? 'var(--surface-1, #06080e)'
                : 'var(--color-footer-bg, var(--surface-1, #06080e))',
            }}
          >
            {displayCount > 99 ? '99+' : displayCount}
          </span>
        )}
      </button>
    </div>
  );
};

export default MiniCart;
